import { useState, useCallback, useRef } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { ListingFilter } from '../../domain/models/Filter';
import type { FiltersService, GetFiltersParams } from '../../application/services/FiltersService';
import { getPlpFilterPreviewTags } from '../../utils/buildPlpFilterPreviewTags';

type SelectedOptions = Record<string, string[]>; // category → [name, ...]

function deriveSelectedOptions(filters: ListingFilter[]): SelectedOptions {
  const result: SelectedOptions = {};
  for (const f of filters) {
    const selected = f.items.filter((i) => i.selected).map((i) => i.name);
    if (selected.length > 0) result[f.category] = selected;
  }
  return result;
}

export interface UsePlpFiltersParams {
  listingFilters: ListingFilter[];
  filtersResetUrl?: string;
  tags: string;
  filterParams: Omit<GetFiltersParams, 'tags'>;
  service: FiltersService;
  navigate: NavigateFunction;
  onApplyInPlace?: (ctx: { applyUrl: string | null }) => void;
}

export interface UsePlpFiltersReturn {
  isOpen: boolean;
  openFilterSheet: () => void;
  previewFilters: ListingFilter[];
  selectedOptions: SelectedOptions;
  applyUrl: string | null;
  isLoadingPreview: boolean;
  toggleOption: (category: string, name: string, url: string) => void;
  handleApply: () => void;
  handleReset: () => void;
  handleClose: () => void;
}

export function usePlpFilters({
  listingFilters,
  filtersResetUrl,
  tags,
  filterParams,
  service,
  navigate,
  onApplyInPlace,
}: UsePlpFiltersParams): UsePlpFiltersReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [previewFilters, setPreviewFilters] = useState<ListingFilter[]>(listingFilters);
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({});
  const [applyUrl, setApplyUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const filtersResetUrlRef = useRef(filtersResetUrl);
  filtersResetUrlRef.current = filtersResetUrl;

  const fetchPreview = useCallback(
    (nextSelected: SelectedOptions, filterItemUrl?: string | null) => {
      const previewTags = getPlpFilterPreviewTags(filterItemUrl, tags);
      setIsLoadingPreview(true);
      service
        .getFilters({ ...filterParams, tags: previewTags, is_for_filter_change: true })
        .then((result) => {
          if (result.filters.length > 0) {
            setPreviewFilters(result.filters);
          }
          if (result.filtersResetUrl) {
            filtersResetUrlRef.current = result.filtersResetUrl;
          }
          const allSelectedNames = Object.values(nextSelected).flat();
          if (allSelectedNames.length === 0) {
            setApplyUrl(filtersResetUrlRef.current ?? null);
          }
        })
        .catch(() => {
          // silently keep current preview filters on error
        })
        .finally(() => {
          setIsLoadingPreview(false);
        });
    },
    [service, tags, filterParams]
  );

  const openFilterSheet = useCallback(() => {
    setPreviewFilters(listingFilters);
    setSelectedOptions(deriveSelectedOptions(listingFilters));
    setApplyUrl(null);
    setIsOpen(true);
  }, [listingFilters]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setPreviewFilters(listingFilters);
    setSelectedOptions(deriveSelectedOptions(listingFilters));
    setApplyUrl(null);
    setIsLoadingPreview(false);
  }, [listingFilters]);

  const toggleOption = useCallback(
    (category: string, name: string, url: string) => {
      const current = selectedOptions[category] ?? [];
      const isSelected = current.includes(name);
      let next: SelectedOptions;
      if (isSelected) {
        const filtered = current.filter((n) => n !== name);
        if (filtered.length === 0) {
          const { [category]: _removed, ...rest } = selectedOptions;
          next = rest;
        } else {
          next = { ...selectedOptions, [category]: filtered };
        }
      } else {
        next = { ...selectedOptions, [category]: [...current, name] };
      }
      setSelectedOptions(next);
      if (url) setApplyUrl(url);
      fetchPreview(next, url);
    },
    [selectedOptions, fetchPreview]
  );

  const handleApply = useCallback(() => {
    if (onApplyInPlace) {
      onApplyInPlace({ applyUrl });
    } else if (applyUrl) {
      navigate(applyUrl);
    }
    setIsOpen(false);
  }, [applyUrl, navigate, onApplyInPlace]);

  const handleReset = useCallback(() => {
    const cleared: SelectedOptions = {};
    setSelectedOptions(cleared);
    setApplyUrl(filtersResetUrlRef.current ?? null);
    fetchPreview(cleared, filtersResetUrlRef.current);
  }, [fetchPreview]);

  return {
    isOpen,
    openFilterSheet,
    previewFilters,
    selectedOptions,
    applyUrl,
    isLoadingPreview,
    toggleOption,
    handleApply,
    handleReset,
    handleClose,
  };
}
